<template>
  <div class="modal-control-toggle">
    <p-toggle
      :model-value="modelValue"
      :label="label"
      :primary-color="primaryColor"
      :disabled="disabled"
      :aria-label="ariaLabel || label"
      @update:model-value="handleToggle"
    />
    <keyboard-shortcut-hint
      v-if="hotkey"
      :shortcut="hotkey"
      theme="gray-medium"
      class="modal-control-toggle__hotkey"
    />
  </div>
</template>

<script setup lang="ts">
import { Colors } from '@/api';
import PToggle from '@/components/core/CoreToggle.vue';
import KeyboardShortcutHint from '@/components/core/KeyboardShortcutHint.vue';

interface Props {
  /** Toggle label text */
  label: string;
  /** Current toggle value */
  modelValue?: boolean;
  /** Theme color */
  primaryColor?: Colors;
  /** Keyboard hotkey to display */
  hotkey?: string;
  /** Whether the toggle is disabled */
  disabled?: boolean;
  /** Optional aria-label override */
  ariaLabel?: string;
}

interface Emits {
  (e: 'update:model-value', value: boolean): void;
}

withDefaults(defineProps<Props>(), {
  modelValue: false,
  primaryColor: Colors.BLUE,
  disabled: false,
});

const emit = defineEmits<Emits>();

function handleToggle(value: boolean) {
  emit('update:model-value', value);
}
</script>

<style lang="scss" scoped>
.modal-control-toggle {
  @apply tw-inline-flex tw-items-center tw-gap-2;

  // Apply font size to the toggle label with deep selector
  :deep(.ds-toggle__label) {
    font-size: 13px;
  }
}

// Override PToggle styles to be more compact in modals
:deep(.p-toggle) {
  .q-toggle__label {
    @apply tw-text-sm;
  }
}
</style>
